import React from "react";
import { motion } from "framer-motion";
import { MapPin, Bookmark, ExternalLink } from "lucide-react";
import { MatchRing } from "@/components/ui-bits";

const MODEL_COLORS = {
  Remote: "text-aqua bg-aqua/10",
  Hybrid: "text-violet bg-violet/10",
  Onsite: "text-coral bg-coral/10",
  "Worldwide Remote": "text-electric bg-electric/10",
  "Remote (worldwide)": "text-electric bg-electric/10",
  "Remote (in-country)": "text-aqua bg-aqua/10",
  "Remote — check eligibility": "text-aqua bg-aqua/10",
  "Onsite/Hybrid": "text-violet bg-violet/10",
  Saved: "text-electric bg-electric/10",
};

const formatPosted = (p) => {
  if (!p) return "Recently";
  const str = String(p).trim();
  if (
    str.toLowerCase().includes("ago") ||
    str.toLowerCase() === "recently" ||
    str.toLowerCase() === "today" ||
    str.toLowerCase() === "yesterday"
  ) {
    return str;
  }
  return `Posted ${str}`;
};

export default function JobCard({ job, onSave, onApply, saved }) {
  const handleApplyClick = () => {
    if (onApply) {
      onApply(job);
    } else {
      const targetUrl =
        job.apply_url && job.apply_url !== "#" ? job.apply_url : (job.link || job.url);
      if (targetUrl && targetUrl !== "#") {
        window.open(targetUrl, "_blank", "noopener,noreferrer");
      }
    }
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="card-surface flex flex-col gap-4 p-5 transition-shadow hover:shadow-card"
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl font-heading text-lg font-black text-white"
            style={{ background: job.color }}>
            {job.logo}
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold leading-tight">{job.role}</h3>
            <p className="text-sm text-muted-foreground">{job.company} · {formatPosted(job.posted)}</p>
          </div>
        </div>
        <MatchRing value={job.match} size={48} stroke={5} color="hsl(var(--chart-4))" />
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{job.location}</span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${MODEL_COLORS[job.model] || "bg-secondary"}`}>{job.model}</span>
        {job.experience && (
          <span className="rounded-full bg-violet/10 text-violet px-2.5 py-0.5 text-xs font-semibold">
            {job.experience}
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {job.skills.map((s) => (
          <span key={s} className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground">{s}</span>
        ))}
      </div>

      <div className="mt-auto flex items-center gap-2 pt-1">
        <button
          onClick={handleApplyClick}
          data-testid={`apply-${job.id}`}
          className="cv-btn-primary flex-1 py-2.5 text-sm inline-flex items-center justify-center gap-2 font-medium"
        >
          <ExternalLink className="h-4 w-4 stroke-[2.2]" />
          <span>Apply</span>
        </button>
        <button
          onClick={() => onSave?.(job)}
          data-testid={`save-${job.id}`}
          className={`rounded-full border p-2.5 transition-colors ${saved ? "border-electric bg-electric/10 text-electric" : "border-border hover:bg-secondary"}`}
        >
          <Bookmark className={`h-4 w-4 ${saved ? "fill-electric" : ""}`} />
        </button>
      </div>
    </motion.div>
  );
}
